/**
 * Aadhaar identity rail — DigiLocker leg (SPEC-05 S4-8; be
 * `session/identityrail`). The journey is a FULL-PAGE redirect to the
 * government/aggregator property (frame-embedding is blocked by those sites,
 * by design) and a same-tab return to the droom origin, which RELAYS
 * state+code to the be callback. The store persists to sessionStorage, so the
 * same-tab round trip rehydrates mid-flow on return.
 *
 * Honest states the caller renders:
 *  - 503 provider-unavailable (`digilocker:not-configured` on the preview) →
 *    a calm fallback: continue, documents verified on the call. Never a dead
 *    end, never a fabricated success.
 *  - 422 state violations (consent missing / session past the pre-live band)
 *    → plain-language, never an error tone.
 */

import { request } from './client';

export interface RailStart {
  railAttemptId: string;
  rail: string;
  state: string;
  redirectUrl: string;
  stateExpiresAt: string | null;
}

export interface RailAttempt {
  railAttemptId: string;
  rail: string;
  attempt: number;
  state: 'STARTED' | 'CALLBACK_RECEIVED' | 'VERIFIED' | 'FAILED' | 'EXPIRED' | string;
  failureReason: string | null;
  verifiedAt: string | null;
}

/**
 * Customer-supplied PRE-FILL hints the hosted-KYC provider (KYCHUB DigiLocker) needs to mint its URL —
 * firstName + mobile seed the government journey. They are NOT the verified identity (that comes back from
 * the DigiLocker consent); the be never persists them to the evidence row. Optional: an OAuth provider
 * ignores them.
 */
export interface DigiLockerHints {
  firstName: string;
  /** Surname PRE-FILL hint. The be sends this real value (or empty) to the CRA — never a fabricated literal
   *  (de-demo P1-C). Like firstName it seeds the journey; the verified surname comes back from DigiLocker. */
  lastName: string;
  mobile: string;
}

export function startDigiLocker(sessionId: string, hints?: DigiLockerHints): Promise<RailStart> {
  return request<RailStart>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/identity-rail/digilocker:start`,
    { method: 'POST', body: hints ?? {} },
  );
}

/**
 * Elect the audited provider-OUTAGE fallback (KYCID·00001-00005-00019). Only meaningful when the be has a
 * server-written FAILED(PROVIDER_UNAVAILABLE:*) attempt on record — the be re-verifies that ledger row and
 * refuses (409) an election that isn't backed by a genuine outage, so the customer cannot forge a bypass of a
 * REQUIRED DigiLocker check. On success the be reports CheckStatus.FALLBACK_VERIFIED (satisfying the REQUIRED
 * plan row) and audits RAIL.DIGILOCKER.FALLBACK_ELECTED; the client advance below is then just UX. 204, no body.
 */
export function electDigiLockerFallback(sessionId: string): Promise<void> {
  return request<void>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/identity-rail/digilocker:fallback`,
    { method: 'POST', body: {} },
  );
}

/**
 * Relay the provider's return to the be — idempotent redeem. `state` is the correlation key (the be finds
 * the attempt by its hash and redeems via the persisted provider txnId); `code` is the OAuth authorisation
 * code where the provider issues one (KYCHUB DigiLocker does not — it round-trips only the state).
 */
export function relayDigiLockerCallback(
  sessionId: string,
  state: string,
  code?: string | null,
): Promise<RailAttempt> {
  // The BE POST callback binds a JSON body (RailCallbackRequest{state, code}), NOT query params —
  // a query-only POST 400s on the missing @RequestBody (KYCID·00001-00005-00009). Send the body.
  return request<RailAttempt>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/identity-rail/digilocker/callback`,
    { method: 'POST', body: { state, code: code ?? null } },
  );
}

/** The session's attempt ladder (data-minimised — no demographics on this wire). */
export function getRailLadder(sessionId: string): Promise<RailAttempt[]> {
  return request<RailAttempt[]>(`/api/v1/sessions/${encodeURIComponent(sessionId)}/identity-rail`);
}
